import * as d3 from 'd3';
import { interpolateSinebow } from 'd3-scale-chromatic';
import { BaseChart, UIState } from '../../js/classes/BaseChart.ts';
import { CwcDataSet } from '../../js/generated/cwc_data.pb.js';

type D3Selection<T extends d3.BaseType> = d3.Selection<T, unknown, null, undefined>;

interface PointData {
  date: Date;
  value: number;
}

interface LineSeries {
  key: string;
  name: string;
  color: string;
  points: PointData[];
  startIndex: number;
}

interface ConsoleWarsUIState extends UIState {
  currentDate: string | null;
}

// UIデータ用のメタデータ定義
interface ConsoleWarsMetaData {
  timeline: string[];
  consoles: { [key: string]: ConsoleInfo };
  seriesOrder: string[];
}

interface ConsoleInfo {
  name: string;
  releaseDate?: string;
}

export class ConsoleWarsChart extends BaseChart<CwcDataSet, LineSeries, ConsoleWarsUIState> {
  static override defaultConfig = {
    // CSVに記載されているキーの表記（大文字・小文字）と完全に一致させる
    defaultVisibleKeys: ['FC', 'SFC', 'N64', 'GC', 'Wii', 'WiiU', 'NS', 'NS2'],
    animationDurationPerSeries: 10000,
    lineWidth: 2.5,
    gridLineWidth: 1,
    highlightOpacity: 1.0,
    dimmedOpacity: 0.15,
    yAxisTicks: 5,
    yAxisTickPadding: 8,
    axisLabelOffsetY: 20,
    tooltipOffsetX: -100,
    tooltipOffsetY: -100,
  };

  private lineData: LineSeries[] = [];
  private seriesOrder: string[] = [];
  private timeline: Date[] = [];
  private timelineStrings: string[] = [];
  private visibleSeriesKeys: Set<string>;

  private animationStartFrame: number = 0;

  // データセットとメタデータ
  protected override fullDataSet: CwcDataSet | null = null;
  private mappingData: ConsoleWarsMetaData | null = null;

  // 描画関連
  private lineCanvas: D3Selection<HTMLCanvasElement> | null = null;
  private lineCtx: CanvasRenderingContext2D | null = null;
  private xScale: d3.ScaleTime<number, number> | null = null;
  private yScale: d3.ScaleLinear<number, number> | null = null;
  private colorScale: d3.ScaleOrdinal<string, string> | null = null;
  private lineGenerator: d3.Line<PointData> | null = null;
  private markersGroup: D3Selection<SVGGElement> | null = null;
  private tooltip: D3Selection<HTMLDivElement> | null = null;
  private hoverGridLinesGroup: d3.Selection<SVGGElement, unknown, null, undefined> | null = null;
  private highlightedKey: string | null = null;

  constructor(chartElement: HTMLElement, config = {}) {
    super(chartElement, config);
    this.visibleSeriesKeys = new Set(this.config.defaultVisibleKeys);
    this.margin = { top: 20, right: 20, bottom: 50, left: 70 };
  }

  /**
   * UI構築用データ（凡例等）を読み込む
   * @returns {Promise<UiDataForInitializer | null>}
   */
  public async loadDataForUI(): Promise<UiDataForInitializer | null> {
    if (this.mappingData) {
      return { seriesOrder: this.mappingData.seriesOrder, consoles: this.mappingData.consoles };
    }

    // Role属性ベースのコンテナからdata属性を取得
    const mappingUrl = (this.chartElement as HTMLElement).dataset.mappingJsonSourceUrl;
    const protoUrl = (this.chartElement as HTMLElement).dataset.protobufSourceUrl;

    if (!mappingUrl || !protoUrl) {
      this._displayMessage('データソースのURLが指定されていません。', 'error');
      console.error('ConsoleWarsChart Error: mappingUrl or protoUrl is missing.');
      return null;
    }

    try {
      const [protoResponse, mappingResponse] = await Promise.all([
        fetch(protoUrl),
        fetch(mappingUrl)
      ]);
      if (!protoResponse.ok) throw new Error(`Protobufの読み込みに失敗: ${protoResponse.statusText}`);
      if (!mappingResponse.ok) throw new Error(`マッピングJSONの読み込みに失敗: ${mappingResponse.statusText}`);

      const [protoBuffer, mappingJson] = await Promise.all([
        protoResponse.arrayBuffer(),
        mappingResponse.json()
      ]);

      const decodedMessage = CwcDataSet.decode(new Uint8Array(protoBuffer));
      this.fullDataSet = CwcDataSet.toObject(decodedMessage, { defaults: true }) as CwcDataSet;
      this.mappingData = mappingJson.meta as ConsoleWarsMetaData;

      return { seriesOrder: this.mappingData.seriesOrder, consoles: this.mappingData.consoles };
    } catch (error) {
      console.error('ConsoleWarsChart CRITICAL ERROR: Failed to load or parse data.', { error });
      this._displayMessage('データファイルの読み込みに失敗しました。', 'error');
      return null;
    }
  }

  public override async init(): Promise<void> {
    // データ未読込ならロード
    if (!this.fullDataSet) {
      await this.loadDataForUI();
    }
    await super.init();
  }

  public setVisibleSeries(visibleKeys: Set<string>): void {
    this.visibleSeriesKeys = visibleKeys;
    this.resetAndRedraw();
  }

  protected override setupDimensions(): void {
    super.setupDimensions();
    // Canvas描画時の滲み防止のため整数化
    this.width = Math.floor(this.width);
    this.height = Math.floor(this.height);
  }

  protected override _processData(): void {
    if (!this.fullDataSet || !this.mappingData) {
      console.error("Data not fully loaded for processing.");
      return;
    }
    const mappingData = this.mappingData;
    const fullDataSet = this.fullDataSet;

    this.seriesOrder = mappingData.seriesOrder;

    const parseTime = d3.timeParse('%Y-%m');
    this.timeline = mappingData.timeline
      .map(d => parseTime(d))
      .filter((d): d is Date => d !== null);

    this.colorScale = d3.scaleOrdinal<string, string>()
      .domain(this.seriesOrder)
      .range(this.seriesOrder.map((_, i) => interpolateSinebow(i / (this.seriesOrder.length - 1))));

    this.lineData = fullDataSet.series.reduce<LineSeries[]>((acc, seriesProto) => {
      const id = seriesProto.id;
      const values = seriesProto.values;
      if (id == null || !Array.isArray(values) || id < 0 || id >= this.seriesOrder.length) {
        return acc;
      }
      const key = this.seriesOrder[id];
      const points: PointData[] = values.map((value, i) => {
        const startIndex = seriesProto.startIndex ?? 0;
        const date = this.timeline[startIndex + i];
        return { date, value };
      });
      acc.push({
        key: key,
        name: mappingData.consoles[key]?.name || key,
        color: this.colorScale!(key),
        points,
        startIndex: seriesProto.startIndex ?? 0
      });
      return acc;
    }, []);

    const formatTime = d3.timeFormat('%Y-%m');
    this.timelineStrings = this.timeline.map(formatTime);
  }

  protected override resetAndRedraw(): void {
    this.pause();

    this.activeLineData = this.seriesOrder
      .filter(key => this.visibleSeriesKeys.has(key))
      .map(key => this.lineData.find(d => d.key === key))
      .filter((d): d is LineSeries => !!d);

    if (this.activeLineData.length > 0) {
      this.animationStartFrame = d3.min(this.activeLineData, d => d.startIndex) ?? 0;
      const animationEndFrame = d3.max(this.activeLineData, d => d.startIndex + d.points.length - 1) ?? this.timeline.length - 1;
      this.totalFrames = animationEndFrame - this.animationStartFrame + 1;
      this.currentFrame = 0;
    } else {
      this.animationStartFrame = 0;
      this.totalFrames = 0;
      this.currentFrame = 0;
    }

    this._initialDrawAxes();

    if (this.activeLineData.length === 0) {
      this.lineCtx?.clearRect(0, 0, this.lineCanvas!.node()!.width, this.lineCanvas!.node()!.height);
      this.markersGroup?.selectAll('*').remove();
      this.updateUI();
      return;
    }

    this.isReady = true;
    this.drawFrame(this.currentFrame);
    this.updateUI();
    if (this.config.autoplay) {
      setTimeout(() => this.play(), this.config.autoplayDelay);
    }
  }

  public override seek(frame: number): void {
    if (!this.isReady) return;
    this.pause();
    this.currentFrame = Math.max(0, Math.min(frame, this.totalFrames - 1));
    this.drawFrame(this.currentFrame);
    this.updateUI();
  }

  protected override setupLayers(): void {
    // 既存レイヤーの削除
    d3.select(this.chartElement).select('canvas').remove();
    d3.select(this.chartElement).select('svg').remove();
    
    const totalWidth = this.width + this.margin.left + this.margin.right;
    const totalHeight = this.height + this.margin.top + this.margin.bottom;

    // Canvasレイヤー作成
    this.lineCanvas = d3.select(this.chartElement).append('canvas')
      .style('width', `${totalWidth}px`).style('height', `${totalHeight}px`);
    this.lineCtx = this.lineCanvas.node()!.getContext('2d');
    
    // 高解像度ディスプレイ対応
    const dpr = window.devicePixelRatio || 1;
    this.lineCanvas.node()!.width = totalWidth * dpr;
    this.lineCanvas.node()!.height = totalHeight * dpr;
    this.lineCtx!.scale(dpr, dpr);

    // SVGレイヤー作成
    this.svg = d3.select(this.chartElement).append('svg')
      .attr('width', totalWidth).attr('height', totalHeight)
      .attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`);
    this.g = this.svg.append('g').attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    // ツールチップのセットアップ (Role属性ベース)
    // ツールチップはグラフ外に配置されることもあるため、コンテナラッパーから探す
    // Role属性がない場合は、新規作成してRole属性を付与する
    const wrapper = this.chartElement.closest<HTMLElement>('[data-chart-role="container"]');
    if (wrapper) {
      // 既存のツールチップがあれば取得、なければ作成
      let tooltip = d3.select(wrapper).select<HTMLDivElement>('.chart-tooltip'); // ここはCSSクラスで探しても良いが、一貫性のためRole推奨
      if (tooltip.empty()) {
        tooltip = d3.select(wrapper).append('div')
            .attr('class', 'chart-tooltip')
            // data-chart-role="tooltip" を付与することを推奨するが、CSSとの兼ね合いでクラスも維持
            .attr('data-chart-role', 'tooltip'); 
      }
      this.tooltip = tooltip;
    }
  }

  protected override _initialDrawAxes(): void {
    if (!this.g || !this.mappingData) return;
    this.g.selectAll('*').remove();
    this.hoverGridLinesGroup = this.g.append('g').attr('class', 'hover-grid-lines');
    
    const allActivePoints = this.activeLineData.flatMap(series => series.points);
    const xDomain = d3.extent(allActivePoints, d => d.date) as [Date, Date];
    this.xScale = d3.scaleTime().domain(xDomain).range([0, this.width]);
    
    const yMax = d3.max(this.activeLineData, s => d3.max(s.points, p => p.value)) || 0;
    this.yScale = d3.scaleLinear().domain([0, yMax]).range([this.height, 0]).nice();
    
    this.lineGenerator = d3.line<PointData>().x(d => this.xScale!(d.date)).y(d => this.yScale!(d.value));
    
    const xAxis = d3.axisBottom(this.xScale).ticks(d3.timeYear.every(5));
    this.g.append('g').attr('class', 'x-axis axis').attr('transform', `translate(0, ${this.height})`).call(xAxis);
    
    const yAxis = d3.axisLeft(this.yScale)
      .ticks(this.config.yAxisTicks)
      .tickFormat(d => `${(d as number) / 1_000_000}M`)
      .tickPadding(this.config.yAxisTickPadding);
    this.g.append('g').attr('class', 'y-axis axis').call(yAxis);
    
    this.markersGroup = this.g.append('g').attr('class', 'markers-group');
    this.g.append('text').attr('class', 'axis-label').attr('transform', 'rotate(-90)').attr('y', 0 - this.margin.left).attr('x', 0 - this.height / 2).attr('dy', '1em').text('普及台数');
  }

  protected override async drawFrame(relativeFrame: number): Promise<void> {
    const absoluteFrame = this.animationStartFrame + relativeFrame;
    this._redrawCanvas(absoluteFrame);
    this._redrawSVG(absoluteFrame);
  }

  private _redrawCanvas(absoluteFrame: number): void {
    if (!this.lineCtx || !this.lineCanvas || !this.lineGenerator || !this.yScale) return;
    this.lineCtx.clearRect(0, 0, this.lineCanvas.node()!.width, this.lineCanvas.node()!.height);
    this.lineCtx.save();
    this.lineCtx.translate(this.margin.left, this.margin.top);
    
    const gridTicks = this.yScale.ticks(this.config.yAxisTicks);
    this.lineCtx.strokeStyle = '#e9e9e9';
    this.lineCtx.lineWidth = this.config.gridLineWidth;
    gridTicks.forEach(tick => {
      if (tick === 0) return;
      this.lineCtx!.beginPath();
      this.lineCtx!.moveTo(0, this.yScale!(tick));
      this.lineCtx!.lineTo(this.width, this.yScale!(tick));
      this.lineCtx!.stroke();
    });

    this.lineGenerator.context(this.lineCtx);
    this.lineCtx.lineCap = 'round';
    this.lineCtx.lineJoin = 'round';
    
    for (const series of this.activeLineData) {
      if (absoluteFrame < series.startIndex || series.points.length === 0) continue;
      const pointsToDraw = series.points.slice(0, absoluteFrame - series.startIndex + 1);
      if (pointsToDraw.length < 2) continue;
      
      this.lineCtx.beginPath();
      this.lineGenerator(pointsToDraw);
      
      const isHighlighted = this.highlightedKey === series.key;
      const isHighlighting = this.highlightedKey !== null;
      const color = d3.color(series.color) as d3.RGBColor;
      
      if (color) {
        color.opacity = !isHighlighting || isHighlighted ? this.config.highlightOpacity : this.config.dimmedOpacity;
        this.lineCtx.strokeStyle = color.toString();
      }
      this.lineCtx.lineWidth = isHighlighted ? this.config.lineWidth * 1.5 : this.config.lineWidth;
      this.lineCtx.stroke();
    }
    
    this.lineGenerator.context(null);
    this.lineCtx.restore();
  }

  private _redrawSVG(absoluteFrame: number): void {
    if (!this.markersGroup || !this.xScale || !this.yScale || !this.mappingData) return;
    const animationEndFrame = this.animationStartFrame + this.totalFrames - 1;
    
    if (absoluteFrame < animationEndFrame) {
      this.markersGroup.selectAll('*').remove();
      return;
    }
    
    this.markersGroup.selectAll('*').remove();
    this.activeLineData.forEach(series => {
      const lastPoint = series.points[series.points.length - 1];
      if (lastPoint) {
        const cx = this.xScale!(lastPoint.date);
        const cy = this.yScale!(lastPoint.value);
        const elapsedMonths = series.points.length - 1;
        
        this.markersGroup!.append('circle')
          .attr('class', 'endpoint-marker')
          .attr('cx', cx).attr('cy', cy).attr('r', 5).style('fill', series.color)
          .attr('data-key', series.key).attr('data-name', series.name)
          .attr('data-value', lastPoint.value).attr('data-months', elapsedMonths)
          .on('mouseover', (event: MouseEvent) => {
            if (this.isPlaying) return;
            const target = d3.select(event.currentTarget as SVGCircleElement);
            target.classed('is-hovered', true).raise();
            this.highlightedKey = target.attr('data-key');
            this._redrawCanvas(absoluteFrame);
            
            this.hoverGridLinesGroup?.selectAll('*').remove();
            this.hoverGridLinesGroup?.append('line').attr('x1', cx).attr('y1', cy).attr('x2', cx).attr('y2', this.height);
            this.hoverGridLinesGroup?.append('line').attr('x1', cx).attr('y1', cy).attr('x2', 0).attr('y2', cy);
            
            this.tooltip?.classed('is-visible', true);
          })
          .on('mousemove', (event: MouseEvent) => {
            if (this.isPlaying || !this.tooltip) return;
            const target = d3.select(event.currentTarget as SVGCircleElement);

            const key = target.attr('data-key');
            if (!key) return;

            const consoleData = this.mappingData?.consoles[key];
            const name = consoleData?.name || key.toUpperCase();
            const releaseDate = consoleData?.releaseDate;

            const value = parseFloat(target.attr('data-value')).toLocaleString();
            const months = target.attr('data-months');

            let tooltipHtml = `<div class="tooltip-name">${name}</div>`;
            if (releaseDate) {
              tooltipHtml += `<div class="tooltip-release-date">発売日: ${releaseDate}</div>`;
            }
            tooltipHtml += `<div class="tooltip-value">${value} <span class="unit">units</span></div>`;
            tooltipHtml += `<div class="tooltip-months">${months} <span class="unit">months</span></div>`;

            this.tooltip.html(tooltipHtml);

            // Role属性ベースでラッパーを取得
            const wrapper = this.chartElement.closest<HTMLElement>('[data-chart-role="container"]');
            if (wrapper) {
              const [mouseX, mouseY] = d3.pointer(event, wrapper);
              this.tooltip.style('left', `${mouseX + this.config.tooltipOffsetX}px`)
                .style('top', `${mouseY + this.config.tooltipOffsetY}px`);
            }
          })
          .on('mouseout', (event: MouseEvent) => {
            if (this.isPlaying) return;
            d3.select(event.currentTarget as SVGCircleElement).classed('is-hovered', false);
            this.highlightedKey = null;
            this._redrawCanvas(absoluteFrame);
            this.hoverGridLinesGroup?.selectAll('*').remove();
            this.tooltip?.classed('is-visible', false);
          });
      }
    });
  }

  protected override updateUI(): void {
    if (typeof this.onUpdateUICallback === 'function') {
      const absoluteFrame = this.animationStartFrame + this.currentFrame;
      const state: ConsoleWarsUIState = {
        isPlaying: this.isPlaying,
        currentFrame: this.currentFrame,
        totalFrames: this.totalFrames,
        currentDate: this.timelineStrings[absoluteFrame] ?? null
      };
      this.onUpdateUICallback(state);
    }
  }
}